import * as React from 'react';

import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import * as z from 'zod';

import { PERMISSION } from 'src/features/auth';
import { useGetParking } from 'src/features/parkingManagement/api/useGetParking.ts';
import EmptyState from 'src/features/parkingManagement/components/EmptyState.tsx';
import ZoneFormFields from 'src/features/parkingManagement/components/forms/ZoneFormFields.tsx';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { useListSubParkings } from 'src/features/subParkings/api/useListSubParkings.ts';
import { useUpdateSubParking } from 'src/features/subParkings/api/useUpdateSubParking.ts';
import useHasPermissions from 'src/hooks/useHasPermissions.ts';
import { useToast } from 'src/registry/hooks/UseToast.ts';
import { Button } from 'src/registry/ui/Button.tsx';
import { Form } from 'src/registry/ui/Form';
import { useTitleStore } from 'src/stores/useTitleStore.ts';
import { generalErrorMessages, generalMessages } from 'src/utils/messages.ts';

const zoneSchema = z.object({
    id: z.string(),
    name: z.string(),
    capacity: z.coerce.number().min(0),
    limit_to_capacity: z.boolean(),
});

const formSchema = z.object({
    zones: z.array(zoneSchema),
});

export type FormSchema = z.infer<typeof formSchema>;

const Zones = () => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParams();
    const { data } = useGetParking({
        parkingId: parkingId || '',
        config: {
            enabled: !!parkingId,
        },
    });

    const {
        data: parkingZones,
        isLoading,
        isError,
        refetch,
    } = useListSubParkings({
        parkingId: parkingId || '',
        config: {
            enabled: !!parkingId,
        },
    });
    const { toast } = useToast();

    const zoneUpdatePermissions = useHasPermissions([PERMISSION.PARKING_DETAILS_MANAGE]);

    const [changedParkingZoneNames, setChangedParkingZoneNames] = React.useState<string[]>([]);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        if (!data) return;
        setTitle(data.name);
        form.reset({
            zones: parkingZones?.results,
        });
        return () => {
            removeTitle();
            form.reset();
        };
    }, [data, formatMessage, parkingZones]);

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            zones: parkingZones?.results,
        },
    });

    const {
        formState: { isDirty, isValid },
    } = form;

    const { mutate, isPending } = useUpdateSubParking({
        onSuccess: () => {
            void refetch();
            {
                changedParkingZoneNames.map((zoneName) => {
                    toast({
                        title: formatMessage(parkingManagementMessages.parkingChangeSuccess),
                        description: formatMessage(parkingManagementMessages.parkingChangeSuccessDetails, {
                            name: zoneName,
                        }),
                    });
                });
            }
            setChangedParkingZoneNames([]);
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = (values: z.infer<typeof formSchema>) => {
        const changedItems = values.zones.filter((zone) => {
            const original = parkingZones?.results.find((parkingZone) => parkingZone.id === zone.id);
            if (!original) return false;

            return (
                zone.name !== original.name ||
                zone.capacity !== original.capacity ||
                zone.limit_to_capacity !== original.limit_to_capacity
            );
        });
        const names = changedItems.map((d) => d.name);
        setChangedParkingZoneNames(names);

        changedItems.forEach((zone) => {
            mutate({
                parkingId: parkingId || '',
                ...zone,
            });
        });
        form.reset(values);
    };

    return (
        <>
            <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
                {!parkingZones?.results && (
                    <EmptyState
                        isLoading={isLoading}
                        isError={isError}
                        parkingName={data?.name}
                        data={parkingZones?.results}
                    />
                )}
                {parkingZones?.results?.map((zone, index) => {
                    return <ZoneFormFields parkingZone={zone} form={form} zoneIndex={index} key={zone.id} />;
                })}
                {parkingZones?.results && (
                    <div className="flex justify-end">
                        <Button
                            className="w-full sm:w-auto"
                            type="submit"
                            disabled={!isDirty || !isValid || !zoneUpdatePermissions}
                        >
                            {isPending && <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin text-lg" />}
                            {formatMessage(generalMessages.saveChanges)}
                        </Button>
                    </div>
                )}
            </Form>
        </>
    );
};

export default Zones;
